/**
 * MOCKUP C — Liquid Glass iOS 26 on Light
 * Test des 3 sections (Hero · Loss Calculator · Map) avec :
 *  - Fond ivoire clair (#FAF7F0)
 *  - Cartes en glassmorphism (backdrop-blur 24px) translucides
 *  - Or = fine ligne d'accent (1px), pas dégradé
 *  - Photos en arrière-plan avec voile blanc 70%
 */
import { Link } from 'react-router-dom';
import {
  ChevronRight, Lock, ShieldCheck, ArrowRight, MessageCircle,
} from 'lucide-react';

const C = {
  ivory: '#FAF7F0',
  ivoryDeep: '#F2EDE2',
  ink: '#1A1714',
  inkBody: '#3D362C',
  inkMute: '#7A6F5F',
  gold: '#A87C4F',
  goldSoft: 'rgba(168,124,79,0.18)',
  brand: '#FF6B35',
  brandSoft: 'rgba(255,107,53,0.08)',
};

const fontDisplay = 'SF Pro Display, -apple-system, system-ui, sans-serif';
const fontSerif = 'Georgia, "Times New Roman", serif';
const fontMono = 'SF Mono, ui-monospace, monospace';

function Shell({ children }) {
  return (
    <div
      className="min-h-screen"
      style={{ background: C.ivory, color: C.ink, fontFamily: fontDisplay, paddingTop: 60 }}
    >
      <div className="mx-auto" style={{ maxWidth: 414 }}>
        {children}
      </div>
    </div>
  );
}

function GlassCard({ children, style = {}, photo = null, overlay = 0.70 }) {
  return (
    <div
      className="relative overflow-hidden"
      style={{
        borderRadius: 22,
        border: `0.5px solid rgba(26,23,20,0.10)`,
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        background: photo
          ? `linear-gradient(180deg, rgba(255,255,255,${overlay}) 0%, rgba(255,255,255,${overlay - 0.05}) 100%)`
          : 'rgba(255,255,255,0.55)',
        boxShadow: '0 12px 28px -14px rgba(26,23,20,0.18), inset 0 1px 0 0 rgba(255,255,255,0.65)',
        ...style,
      }}
    >
      {photo && (
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${photo})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px)',
            transform: 'scale(1.05)',
          }}
        />
      )}
      {children}
    </div>
  );
}

// ─── HERO ────────────────────────────────────────────────────────────
function HeroC() {
  return (
    <div className="px-4 mb-4">
      <GlassCard
        style={{ padding: '20px 18px 18px' }}
      >
        {/* Top row: avatar + meta + lock badge */}
        <div className="flex items-start gap-3 mb-3">
          <img
            src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/0fmy06l5_avatar-sabril.png"
            alt="Sabril"
            style={{ width: 44, height: 44, borderRadius: 999, border: `0.5px solid ${C.goldSoft}` }}
          />
          <div className="flex-1 min-w-0">
            <p style={{ fontFamily: fontDisplay, fontSize: 13, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>
              Sabril Jurczeszyn
            </p>
            <p style={{ fontFamily: fontMono, fontSize: 9, color: C.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 2 }}>
              Direction de marque · 2018 → ∞
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1"
            style={{
              background: 'rgba(168,124,79,0.10)',
              border: `0.5px solid ${C.goldSoft}`,
              color: C.gold,
              borderRadius: 999,
              padding: '4px 9px',
              fontFamily: fontMono,
              fontSize: 8.5,
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
            3 places
          </span>
        </div>

        {/* Overline tagline */}
        <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 12, color: C.inkBody, marginBottom: 8 }}>
          Le cercle des restos qui deviennent des marques.
        </p>

        {/* HUGE title */}
        <h1
          style={{
            fontFamily: fontDisplay,
            fontSize: 52,
            fontWeight: 900,
            color: C.ink,
            letterSpacing: '-0.035em',
            lineHeight: 0.95,
            marginBottom: 12,
          }}
        >
          Salon<br />
          <span style={{ color: C.gold }}>Privé</span>
          <span style={{ color: C.brand }}>.</span>
        </h1>

        {/* Fine gold separator line */}
        <div style={{ height: 0.5, background: C.gold, opacity: 0.4, margin: '6px 0 12px' }} />

        <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 13.5, color: C.ink, lineHeight: 1.5, marginBottom: 6 }}>
          « Vous avez le restaurant. Je donne <span style={{ color: C.gold, fontWeight: 600 }}>la marque</span>. »
        </p>
        <p style={{ fontFamily: fontDisplay, fontSize: 12, color: C.inkBody, lineHeight: 1.45 }}>
          Cercle privé des restaurateurs qui veulent transformer leur resto en{' '}
          <span style={{ color: C.ink, fontWeight: 600 }}>une marque dont ils sont fiers</span>. Rentable. Transmissible.
        </p>

        {/* 3 KPI in glass mini cards */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { v: '6',  l: 'restos\naccompagnés', tone: C.brand },
            { v: '4',  l: 'villes · 4 pays',     tone: C.gold },
            { v: '12h',l: 'coaching inclus',     tone: '#1FA644' },
          ].map((k) => (
            <div
              key={k.l}
              style={{
                background: 'rgba(255,255,255,0.65)',
                backdropFilter: 'blur(12px)',
                borderRadius: 12,
                border: '0.5px solid rgba(26,23,20,0.08)',
                padding: '10px 8px',
                textAlign: 'center',
              }}
            >
              <p style={{ fontFamily: fontDisplay, fontSize: 22, fontWeight: 800, color: k.tone, letterSpacing: '-0.02em', lineHeight: 1 }}>
                {k.v}
              </p>
              <p style={{ fontFamily: fontMono, fontSize: 7.5, color: C.inkMute, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 4, lineHeight: 1.2, whiteSpace: 'pre-line' }}>
                {k.l}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-stretch gap-2 mt-4">
          <button
            style={{
              flex: 1,
              background: C.ink,
              color: C.ivory,
              borderRadius: 999,
              padding: '11px 16px',
              fontFamily: fontDisplay,
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            Candidater au Salon
          </button>
          <button
            style={{
              background: 'rgba(255,255,255,0.7)',
              color: C.gold,
              borderRadius: 999,
              border: `0.5px solid ${C.goldSoft}`,
              padding: '11px 14px',
              fontFamily: fontDisplay,
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Offres <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </GlassCard>
    </div>
  );
}

// ─── LOSS CALCULATOR ─────────────────────────────────────────────────
function LossC() {
  return (
    <div className="px-4 mb-4">
      <p style={{ fontFamily: fontMono, fontSize: 9, color: C.inkMute, letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 6 }}>
        Calculateur · Réalité chiffrée
      </p>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: '-0.025em', lineHeight: 1.05, marginBottom: 14 }}>
        Combien tu perds<br/><span style={{ color: C.brand }}>chaque mois.</span>
      </h2>

      <GlassCard style={{ padding: '16px 16px 14px' }}>
        {/* Slider 1 */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span style={{ fontFamily: fontMono, fontSize: 9.5, color: C.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
              CA mensuel
            </span>
            <span style={{ fontFamily: fontDisplay, fontSize: 22, fontWeight: 800, color: C.gold, letterSpacing: '-0.02em' }}>
              60 K€
            </span>
          </div>
          <div className="relative h-1.5 rounded-full" style={{ background: C.ivoryDeep }}>
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: '36%', background: C.gold }} />
            <div className="absolute" style={{ left: '36%', top: '-7px', width: 18, height: 18, borderRadius: 999, background: '#fff', border: `1.5px solid ${C.gold}`, boxShadow: '0 2px 6px rgba(0,0,0,0.10)', marginLeft: -9 }} />
          </div>
        </div>

        {/* Slider 2 */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span style={{ fontFamily: fontMono, fontSize: 9.5, color: C.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
              Food cost actuel
            </span>
            <span style={{ fontFamily: fontDisplay, fontSize: 22, fontWeight: 800, color: C.brand, letterSpacing: '-0.02em' }}>
              38 %
            </span>
          </div>
          <div className="relative h-1.5 rounded-full" style={{ background: C.ivoryDeep }}>
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: '65%', background: C.brand }} />
            <div className="absolute" style={{ left: '65%', top: '-7px', width: 18, height: 18, borderRadius: 999, background: '#fff', border: `1.5px solid ${C.brand}`, boxShadow: '0 2px 6px rgba(0,0,0,0.10)', marginLeft: -9 }} />
          </div>
          <p style={{ fontFamily: fontMono, fontSize: 8.5, color: C.inkMute, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 5, textAlign: 'center' }}>
            Cible saine du métier · 27%
          </p>
        </div>

        {/* Result */}
        <div
          style={{
            background: 'linear-gradient(180deg, rgba(255,107,53,0.06) 0%, rgba(255,107,53,0.02) 100%)',
            border: `0.5px solid rgba(255,107,53,0.22)`,
            borderRadius: 14,
            padding: '14px 14px',
            marginTop: 8,
          }}
        >
          <p style={{ fontFamily: fontMono, fontSize: 9, color: C.brand, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Tu laisses chaque mois
          </p>
          <p style={{ fontFamily: fontDisplay, fontSize: 46, fontWeight: 900, color: C.brand, letterSpacing: '-0.035em', lineHeight: 1 }}>
            6 600 €
          </p>
          <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 12, color: C.inkBody, marginTop: 8, lineHeight: 1.45 }}>
            Soit <span style={{ color: C.ink, fontWeight: 600, fontFamily: fontDisplay, fontStyle: 'normal' }}>79 200 €</span> par an qui partent en fumée — sans rien faire d&apos;autre.
          </p>
        </div>

        {/* Récupérable */}
        <div
          style={{
            background: 'rgba(31,166,68,0.08)',
            border: '0.5px solid rgba(31,166,68,0.25)',
            borderRadius: 12,
            padding: '10px 12px',
            marginTop: 10,
          }}
        >
          <p style={{ fontFamily: fontMono, fontSize: 8.5, color: '#1FA644', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 800 }}>
            Récupérable avec le programme
          </p>
          <p style={{ fontFamily: fontDisplay, fontSize: 22, fontWeight: 800, color: '#1FA644', letterSpacing: '-0.02em', marginTop: 2 }}>
            +6 600 € / mois
          </p>
        </div>

        <button
          style={{
            width: '100%',
            background: C.ink,
            color: C.ivory,
            borderRadius: 999,
            padding: '12px 16px',
            fontFamily: fontDisplay,
            fontSize: 13.5,
            fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 12,
          }}
        >
          <MessageCircle className="w-4 h-4" />
          En parler à Sabril maintenant
        </button>
      </GlassCard>
    </div>
  );
}

// ─── MAP ─────────────────────────────────────────────────────────────
// Stylized GLOBE on light ivory + Liquid Glass labels
function MapC() {
  // Positions on a 360×360 viewBox, globe centered at (180,180) r=140
  const CITIES = [
    { name: 'Miami',     country: 'États-Unis', count: '1/3', tone: '#1FA644', x: 80,  y: 160, lx: 14,  ly: 158, side: 'right' },
    { name: 'Marbella',  country: 'Espagne',    count: '2/2', tone: C.brand,   x: 158, y: 150, lx: 14,  ly: 78,  side: 'right' },
    { name: 'Cannes',    country: 'France',     count: '1/2', tone: '#1FA644', x: 188, y: 130, lx: 248, ly: 56,  side: 'left'  },
    { name: 'Marrakech', country: 'Maroc',      count: '6/6', tone: C.brand,   x: 168, y: 188, lx: 248, ly: 298, side: 'left'  },
  ];

  // Simplified continent paths positioned on the globe (relative to globe coords)
  // The globe is centered at (180, 180), r=140. We "drape" simplified continents on it.
  const continentsSvg = `
    <!-- North America (left-top quadrant of globe) -->
    <path d="M 56,118 C 70,108 90,104 108,110 C 124,115 138,124 146,138 L 152,156 C 156,172 154,188 144,200 C 132,212 116,218 100,216 C 84,212 70,200 62,184 C 56,170 54,150 56,134 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Florida hook -->
    <path d="M 124,160 C 130,170 132,184 128,194 C 124,202 116,202 112,194 C 110,184 114,170 122,160 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- South America (lower portion) -->
    <path d="M 122,222 C 138,222 152,234 156,250 C 158,266 152,282 142,290 C 130,296 116,290 108,278 C 102,262 104,246 116,232 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Europe (top center-right) -->
    <path d="M 170,108 C 188,104 210,106 222,114 C 230,122 232,132 224,140 C 212,146 194,146 178,142 C 168,138 162,128 166,118 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Iberian Peninsula -->
    <path d="M 156,144 C 162,142 170,146 172,154 L 170,168 C 166,172 158,172 154,164 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Britain -->
    <path d="M 162,96 C 168,93 174,95 174,102 C 174,108 168,112 162,108 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Scandinavia -->
    <path d="M 190,80 C 200,76 212,78 218,86 C 222,96 218,104 208,106 C 198,104 188,96 188,86 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- North Africa (large central) -->
    <path d="M 156,180 C 178,176 208,176 232,180 C 254,184 268,194 268,210 C 266,228 246,236 220,236 C 196,236 174,232 158,224 C 148,216 146,200 152,188 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- West Africa / Sub-Saharan -->
    <path d="M 188,242 C 208,242 224,254 228,272 C 230,288 220,300 204,302 C 184,298 170,284 168,266 C 168,254 176,244 188,242 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
    <!-- Italy -->
    <path d="M 200,148 C 204,156 204,168 202,178 C 200,184 196,184 195,178 C 194,166 196,154 200,148 Z"
          fill="rgba(168,124,79,0.40)" stroke="rgba(168,124,79,0.55)" stroke-width="0.5"/>
  `;

  // Connection arcs: Miami → Marrakech → Marbella → Cannes
  const order = ['Miami', 'Marrakech', 'Marbella', 'Cannes'];
  const arcs = order.slice(0, -1).map((nA, i) => {
    const a = CITIES.find((c) => c.name === nA);
    const b = CITIES.find((c) => c.name === order[i + 1]);
    const cx = (a.x + b.x) / 2;
    const cy = Math.min(a.y, b.y) - 18;
    return `<path d="M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}" stroke="${C.brand}" stroke-width="1.4" fill="none" stroke-dasharray="4 4" opacity="0.70"/>`;
  }).join('');

  const svgInner = `
    <defs>
      <radialGradient id="globe-c" cx="38%" cy="32%" r="78%">
        <stop offset="0%"   stop-color="#FFFFFF" stop-opacity="0.95"/>
        <stop offset="55%"  stop-color="#F2EDE2" stop-opacity="0.90"/>
        <stop offset="100%" stop-color="#E5DCC9" stop-opacity="0.85"/>
      </radialGradient>
      <radialGradient id="globe-spec-c" cx="30%" cy="22%" r="35%">
        <stop offset="0%"   stop-color="#FFFFFF" stop-opacity="0.55"/>
        <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="globe-rim-c" cx="50%" cy="50%" r="50%">
        <stop offset="92%"  stop-color="rgba(168,124,79,0)" stop-opacity="0"/>
        <stop offset="99%"  stop-color="rgba(168,124,79,0.45)" stop-opacity="0.45"/>
        <stop offset="100%" stop-color="rgba(168,124,79,0)" stop-opacity="0"/>
      </radialGradient>
    </defs>

    <!-- Globe body -->
    <circle cx="180" cy="180" r="140" fill="url(#globe-c)" stroke="rgba(168,124,79,0.20)" stroke-width="0.5"/>
    <!-- Highlight -->
    <circle cx="180" cy="180" r="140" fill="url(#globe-spec-c)"/>
    <!-- Outer rim (gold ghost) -->
    <circle cx="180" cy="180" r="150" fill="url(#globe-rim-c)"/>

    <!-- Latitude ellipses (parallels) -->
    <g stroke="rgba(168,124,79,0.25)" stroke-width="0.5" fill="none">
      ${[40, 70, 100, 125, 140, 125, 100, 70, 40].map((rx, i) => `<ellipse cx="180" cy="${60 + i * 30}" rx="${rx}" ry="3"/>`).join('')}
    </g>

    <!-- Longitude curves -->
    <g stroke="rgba(168,124,79,0.20)" stroke-width="0.5" fill="none">
      ${[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const rx = Math.max(8, 140 - Math.abs(i - 3) * 42);
        const isPrime = i === 3;
        return `<ellipse cx="180" cy="180" rx="${rx}" ry="140" stroke="rgba(168,124,79,${isPrime ? 0.35 : 0.18})" stroke-width="${isPrime ? 0.7 : 0.5}"/>`;
      }).join('')}
    </g>

    <!-- Continents (clipped to globe via mask) -->
    <defs>
      <clipPath id="globe-clip">
        <circle cx="180" cy="180" r="140"/>
      </clipPath>
    </defs>
    <g clip-path="url(#globe-clip)">
      ${continentsSvg}
    </g>

    <!-- Connection arcs -->
    ${arcs}

    <!-- City pins -->
    ${CITIES.map((c) => `
      <g>
        <circle cx="${c.x}" cy="${c.y}" r="14" fill="${c.tone}" opacity="0.14"/>
        <circle cx="${c.x}" cy="${c.y}" r="8"  fill="${c.tone}" opacity="0.28"/>
        <circle cx="${c.x}" cy="${c.y}" r="4.5" fill="${c.tone}"/>
        <circle cx="${c.x}" cy="${c.y}" r="4.5" fill="none" stroke="#FFFFFF" stroke-width="1.4"/>
        <line x1="${c.x}" y1="${c.y}" x2="${c.side === 'right' ? c.x + 18 : c.x - 18}" y2="${c.ly}" stroke="${c.tone}" stroke-width="0.5" opacity="0.35"/>
      </g>
    `).join('')}
  `;

  return (
    <div className="px-4 mb-6">
      <p style={{ fontFamily: fontMono, fontSize: 9, color: C.inkMute, letterSpacing: '0.20em', textTransform: 'uppercase', marginBottom: 6 }}>
        Réseau international · 4 villes
      </p>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: '-0.025em', lineHeight: 1.05, marginBottom: 8 }}>
        Là où je travaille.
      </h2>
      <p style={{ fontFamily: fontDisplay, fontSize: 12.5, color: C.inkBody, lineHeight: 1.5, marginBottom: 14 }}>
        De Cannes à Miami — un circuit où je construis des marques rentables.{' '}
        <span style={{ color: C.ink, fontWeight: 600 }}>10 restos accompagnés · 3 places restantes.</span>
      </p>

      {/* Map area — no card frame, transparent over ivory */}
      <div className="relative mx-auto" style={{ width: '100%', maxWidth: 380, aspectRatio: '1 / 1' }}>
        <svg
          viewBox="0 0 360 360"
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 w-full h-full"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: svgInner }}
        />

        {/* Liquid Glass labels positioned in % of viewBox 360×360 */}
        {CITIES.map((c) => {
          const xPct = (c.lx / 360) * 100;
          const yPct = (c.ly / 360) * 100;
          const isRight = c.side === 'right';
          return (
            <div
              key={`lbl-${c.name}`}
              className="absolute"
              style={{
                left: `${xPct}%`,
                top: `${yPct}%`,
                transform: `translate(${isRight ? '0' : '-100%'}, -50%)`,
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  background: 'rgba(255,255,255,0.62)',
                  backdropFilter: 'blur(22px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(22px) saturate(180%)',
                  borderRadius: 10,
                  border: '0.5px solid rgba(26,23,20,0.08)',
                  padding: '6px 10px',
                  boxShadow: '0 8px 18px -10px rgba(26,23,20,0.20), inset 0 1px 0 0 rgba(255,255,255,0.70)',
                  minWidth: 96,
                }}
              >
                <p style={{ fontFamily: fontDisplay, fontSize: 11.5, fontWeight: 700, color: C.ink, lineHeight: 1, letterSpacing: '-0.01em' }}>
                  {c.name}
                </p>
                <p style={{ fontFamily: fontMono, fontSize: 7, color: C.inkMute, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 3 }}>
                  {c.country}
                </p>
                <span
                  className="inline-block"
                  style={{
                    marginTop: 4,
                    background: `${c.tone}18`,
                    color: c.tone,
                    fontFamily: fontMono,
                    fontSize: 8,
                    fontWeight: 800,
                    letterSpacing: '0.10em',
                    padding: '1.5px 5px',
                    borderRadius: 4,
                  }}
                >
                  {c.count} restos
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4 px-1" style={{ borderTop: `0.5px solid ${C.ink}10`, paddingTop: 10 }}>
        <span style={{ fontFamily: fontMono, fontSize: 9, color: C.gold, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 800 }}>
          10 / 13 places prises
        </span>
        <span style={{ fontFamily: fontMono, fontSize: 9, color: C.inkMute, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          4 villes · 13 slots
        </span>
      </div>
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────
export default function MockupC() {
  return (
    <Shell>
      {/* Top label */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <span style={{ fontFamily: fontMono, fontSize: 9.5, color: C.gold, letterSpacing: '0.20em', textTransform: 'uppercase', fontWeight: 800 }}>
          Direction C · Liquid Glass
        </span>
        <Link to="/prsnl" style={{ fontFamily: fontDisplay, fontSize: 11, color: C.inkMute, fontWeight: 600 }}>
          ← Retour
        </Link>
      </div>
      <HeroC />
      <LossC />
      <MapC />

      <p className="px-4 pb-8 text-center" style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 11.5, color: C.inkMute }}>
        Aperçu Direction C — Liquid Glass iOS 26 sur fond ivoire
      </p>
    </Shell>
  );
}
